import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Settings, Mail, Server, Save, Trash2, Plus, Check, X, Loader2,
  Eye, EyeOff, RefreshCw, FileText, Send, Copy, ChevronDown, ChevronUp,
  Sparkles, User, Hash, Calendar, MapPin, Type, Briefcase, Percent, Euro
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { atencionClienteAPI } from '../api';
import { toast } from '../hooks/use-toast';
import RichTextEditor from '../components/RichTextEditor';

const ConfiguracionAtencionCliente = () => {
  const [activeTab, setActiveTab] = useState('smtp');
  // v37: si llega ?tab=plantillas-automatizacion en URL, abre directamente esa pestaña
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['smtp', 'plantillas', 'plantillas-automatizacion'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);
  // v37: categorías por sub-pestaña de plantillas
  const CAT_ATENCION = useMemo(() => ['general', 'atencion_cliente', 'honorarios'], []);
  const CAT_AUTOMATIZACION = useMemo(() => ['sentencia', 'recurso_apelacion_banco', 'senalamiento'], []);
  
  // Config Email
  const [configsEmail, setConfigsEmail] = useState([]);
  const [loadingEmail, setLoadingEmail] = useState(true);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  
  const [emailForm, setEmailForm] = useState({
    nombre: '',
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: false,
    smtp_use_ssl: true,
    email_from: '',
    nombre_from: 'LEXDUO'
  });

  // Plantillas
  const [plantillas, setPlantillas] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);
  const [isPlantillaDialogOpen, setIsPlantillaDialogOpen] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [savingPlantilla, setSavingPlantilla] = useState(false);
  const [variablesOpen, setVariablesOpen] = useState(true);  // v38: panel variables expandible
  
  const [plantillaForm, setPlantillaForm] = useState({
    nombre: '',
    descripcion: '',
    asunto: '',
    cuerpo: '',
    categoria: 'general',
    idioma: 'ca',
    activa: true,
    orden: 0
  });

  // v38: catalogo visual de variables disponibles
  const VARIABLES_CATALOG = useMemo(() => [
    { var: 'cliente_nombre',     icon: User,      label: 'Nombre cliente',         desc: 'Nombre + apellidos del cliente del expediente', categories: '*',                                             color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { var: 'expediente_numero',  icon: Hash,      label: 'Núm. expediente',        desc: 'Código del expediente (ej. Q-EXP8032)',         categories: '*',                                             color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { var: 'titulo',             icon: Type,      label: 'Título IA',              desc: 'Título resumido del trámite detectado por IA',   categories: 'sentencia,recurso_apelacion_banco,senalamiento', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    { var: 'resumen_ia',         icon: Sparkles,  label: 'Resumen IA',             desc: 'Análisis IA del documento (fallo, contenido…)', categories: 'sentencia,recurso_apelacion_banco,senalamiento', color: 'bg-amber-100 text-amber-700 border-amber-200',  highlight: true },
    { var: 'fecha_senalamiento', icon: Calendar,  label: 'Fecha señalamiento',     desc: 'Fecha del juicio/señalamiento (DD/MM/YYYY)',     categories: 'senalamiento',                                  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { var: 'juzgado',            icon: MapPin,    label: 'Juzgado',                desc: 'Órgano judicial del expediente',                 categories: 'senalamiento',                                  color: 'bg-rose-100 text-rose-700 border-rose-200' },
    { var: 'fecha',              icon: Calendar,  label: 'Fecha de hoy',           desc: 'Fecha actual al generar el email (DD/MM/YYYY)',  categories: '*',                                             color: 'bg-slate-100 text-slate-700 border-slate-200' },
    { var: 'importe_reclamado',     icon: Euro,    label: 'Importe reclamado',  desc: 'Importe reclamado en honorarios',     categories: 'honorarios', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { var: 'porcentaje_honorarios', icon: Percent, label: '% honorarios',       desc: 'Porcentaje de honorarios pactado',    categories: 'honorarios', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { var: 'importe_honorarios',    icon: Briefcase, label: 'Importe honorarios', desc: 'Honorarios calculados',             categories: 'honorarios', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  ], []);

  const variablesParaCategoria = (categoria) => VARIABLES_CATALOG.filter(v =>
    v.categories === '*' || v.categories.split(',').includes(categoria)
  );

  const copiarVariable = (varName) => {
    const token = `{{${varName}}}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(token).then(() => {
        toast({ title: 'Copiado al portapapeles', description: token, duration: 1800 });
      }).catch(() => {
        toast({ title: 'Variable', description: token });
      });
    } else {
      toast({ title: 'Variable', description: token });
    }
  };

  useEffect(() => {
    loadConfigsEmail();
    loadPlantillas();
  }, []);

  const loadConfigsEmail = async () => {
    setLoadingEmail(true);
    try {
      // Primero intentar obtener o crear la configuración por defecto
      await atencionClienteAPI.getDefaultConfig();
      // Luego cargar todas las configuraciones
      const res = await atencionClienteAPI.getConfigEmail();
      setConfigsEmail(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo cargar la configuración', variant: 'destructive' });
    } finally {
      setLoadingEmail(false);
    }
  };

  const loadPlantillas = async () => {
    setLoadingPlantillas(true);
    try {
      const res = await atencionClienteAPI.getPlantillas();
      setPlantillas(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron cargar las plantillas', variant: 'destructive' });
    } finally {
      setLoadingPlantillas(false);
    }
  };

  // ================== Email Config ==================
  const resetEmailForm = () => {
    setEmailForm({
      nombre: '',
      smtp_host: '',
      smtp_port: 465,
      smtp_user: '',
      smtp_password: '',
      smtp_use_tls: false,
      smtp_use_ssl: true,
      email_from: '',
      nombre_from: 'LEXDUO'
    });
    setEditingConfig(null);
    setShowPassword(false);
  };

  const handleOpenNewEmail = () => {
    resetEmailForm();
    setIsEmailDialogOpen(true);
  };

  const handleEditEmail = (config) => {
    setEmailForm({
      nombre: config.nombre || '',
      smtp_host: config.smtp_host || '',
      smtp_port: config.smtp_port || 465,
      smtp_user: config.smtp_user || '',
      smtp_password: '', // No mostrar la contraseña real
      smtp_use_tls: config.smtp_use_tls || false,
      smtp_use_ssl: config.smtp_use_ssl || true,
      email_from: config.email_from || '',
      nombre_from: config.nombre_from || 'LEXDUO'
    });
    setEditingConfig(config);
    setIsEmailDialogOpen(true);
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.smtp_host || !emailForm.smtp_user || !emailForm.email_from) {
      toast({ title: 'Error', description: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }
    
    setSavingEmail(true);
    try {
      const data = { ...emailForm };
      // Si está editando y no cambió la contraseña, no enviarla
      if (editingConfig && !data.smtp_password) {
        delete data.smtp_password;
      }
      
      if (editingConfig) {
        await atencionClienteAPI.updateConfigEmail(editingConfig.id, data);
        toast({ title: 'Configuración actualizada' });
      } else {
        await atencionClienteAPI.createConfigEmail(data);
        toast({ title: 'Configuración creada' });
      }
      
      setIsEmailDialogOpen(false);
      resetEmailForm();
      loadConfigsEmail();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleDeleteEmail = async (config) => {
    if (!window.confirm('¿Está seguro de eliminar esta configuración?')) return;
    try {
      await atencionClienteAPI.deleteConfigEmail(config.id);
      toast({ title: 'Configuración eliminada' });
      loadConfigsEmail();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleTestEmail = async (config) => {
    setTesting(true);
    try {
      const res = await atencionClienteAPI.testConfigEmail(config.id);
      if (res.data.success) {
        toast({ title: '¡Prueba exitosa!', description: 'El email de prueba se envió correctamente' });
        loadConfigsEmail();
      } else {
        toast({ title: 'Error de envío', description: res.data.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo enviar el email de prueba', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleToggleActivo = async (config) => {
    try {
      await atencionClienteAPI.updateConfigEmail(config.id, { activo: !config.activo });
      loadConfigsEmail();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  // ================== Plantillas ==================
  const resetPlantillaForm = () => {
    setPlantillaForm({
      nombre: '',
      descripcion: '',
      asunto: '',
      cuerpo: '',
      categoria: 'general',
      idioma: 'ca',
      activa: true,
      orden: 0
    });
    setEditingPlantilla(null);
  };

  const handleOpenNewPlantilla = () => {
    resetPlantillaForm();
    setIsPlantillaDialogOpen(true);
  };

  const handleEditPlantilla = (plantilla) => {
    setPlantillaForm({
      nombre: plantilla.nombre || '',
      descripcion: plantilla.descripcion || '',
      asunto: plantilla.asunto || '',
      cuerpo: plantilla.cuerpo || '',
      categoria: plantilla.categoria || 'general',
      idioma: plantilla.idioma || 'ca',
      activa: plantilla.activa !== false,
      orden: plantilla.orden || 0
    });
    setEditingPlantilla(plantilla);
    setIsPlantillaDialogOpen(true);
  };

  const handleSubmitPlantilla = async (e) => {
    e.preventDefault();
    if (!plantillaForm.nombre || !plantillaForm.asunto || !plantillaForm.cuerpo) {
      toast({ title: 'Error', description: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }
    
    setSavingPlantilla(true);
    try {
      if (editingPlantilla) {
        await atencionClienteAPI.updatePlantilla(editingPlantilla.id, plantillaForm);
        toast({ title: 'Plantilla actualizada' });
      } else {
        await atencionClienteAPI.createPlantilla(plantillaForm);
        toast({ title: 'Plantilla creada' });
      }
      
      setIsPlantillaDialogOpen(false);
      resetPlantillaForm();
      loadPlantillas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSavingPlantilla(false);
    }
  };

  const handleDeletePlantilla = async (plantilla) => {
    if (!window.confirm('¿Está seguro de eliminar esta plantilla?')) return;
    try {
      await atencionClienteAPI.deletePlantilla(plantilla.id);
      toast({ title: 'Plantilla eliminada' });
      loadPlantillas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center">
          <Settings className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Configuración Atención Cliente</h1>
          <p className="text-gray-600">Gestión de servidores SMTP y plantillas de email</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            Servidor SMTP
          </TabsTrigger>
          <TabsTrigger value="plantillas" className="flex items-center gap-2" data-testid="tab-plantillas-atencion">
            <FileText className="w-4 h-4" />
            Plantillas Atención
          </TabsTrigger>
          <TabsTrigger value="plantillas-automatizacion" className="flex items-center gap-2" data-testid="tab-plantillas-automatizacion">
            <FileText className="w-4 h-4" />
            Plantillas Automatización
          </TabsTrigger>
        </TabsList>

        {/* ================ TAB SMTP ================ */}
        <TabsContent value="smtp" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-600">Configure el servidor SMTP para enviar emails a los clientes</p>
            <Button onClick={handleOpenNewEmail} className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Configuración
            </Button>
          </div>

          {loadingEmail ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : configsEmail.length > 0 ? (
            <div className="grid gap-4">
              {configsEmail.map((config) => (
                <Card key={config.id} className={!config.activo ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.activo ? 'bg-cyan-100' : 'bg-gray-100'}`}>
                          <Mail className={`w-5 h-5 ${config.activo ? 'text-cyan-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {config.nombre}
                            {config.verificado && (
                              <Badge className="bg-green-100 text-green-700">
                                <Check className="w-3 h-3 mr-1" /> Verificado
                              </Badge>
                            )}
                            {!config.activo && (
                              <Badge className="bg-gray-100 text-gray-500">Inactivo</Badge>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600">{config.email_from}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {config.smtp_host}:{config.smtp_port} 
                            ({config.smtp_use_ssl ? 'SSL' : config.smtp_use_tls ? 'TLS' : 'Sin cifrado'})
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={config.activo}
                          onCheckedChange={() => handleToggleActivo(config)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestEmail(config)}
                          disabled={testing}
                        >
                          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditEmail(config)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteEmail(config)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Server className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No hay servidores SMTP configurados</p>
                <Button variant="outline" className="mt-4" onClick={handleOpenNewEmail}>
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir configuración
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ================ TAB PLANTILLAS (atención + automatización) ================
            v37: una sola sección que filtra por categoría según la sub-pestaña activa.
            Usamos forceMount + className condicional para que ambos TabsTrigger
            ('plantillas' y 'plantillas-automatizacion') compartan el mismo render. */}
        <TabsContent
          value="plantillas"
          forceMount
          className={
            (activeTab === 'plantillas' || activeTab === 'plantillas-automatizacion')
              ? 'mt-6 space-y-4'
              : 'hidden'
          }
        >
          {(() => {
            const isAuto = activeTab === 'plantillas-automatizacion';
            const catsVisibles = isAuto ? CAT_AUTOMATIZACION : CAT_ATENCION;
            const plantillasFiltradas = plantillas.filter(p => catsVisibles.includes(p.categoria || 'general'));
            return (
              <>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-600">
                      {isAuto
                        ? 'Plantillas para notificaciones automatizadas (emails procuradores → cliente)'
                        : 'Plantillas predefinidas para respuestas a clientes'}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Variables: {"{{cliente_nombre}}"}, {"{{expediente_numero}}"}, {"{{fecha}}"}
                      {isAuto && <>, {"{{titulo}}"}, <span className="text-amber-600 font-semibold">{"{{resumen_ia}}"}</span> <span className="text-amber-600">(análisis IA del documento)</span>, {"{{fecha_senalamiento}}"}, {"{{juzgado}}"}</>}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      // Pre-seleccionar la categoría según la sub-pestaña activa
                      resetPlantillaForm();
                      setPlantillaForm(prev => ({ ...prev, categoria: isAuto ? 'sentencia' : 'general' }));
                      setIsPlantillaDialogOpen(true);
                    }}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="btn-nueva-plantilla"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Plantilla
                  </Button>
                </div>

                {loadingPlantillas ? (
                  <div className="py-12 flex justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : plantillasFiltradas.length > 0 ? (
                  <div className="grid gap-4">
                    {plantillasFiltradas.map((plantilla) => (
                <Card key={plantilla.id} className={!plantilla.activa ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2 flex-wrap">
                          {plantilla.nombre}
                          {plantilla.idioma && (
                            <Badge variant="outline" className="text-xs uppercase">{plantilla.idioma}</Badge>
                          )}
                          {plantilla.categoria === 'honorarios' && (
                            <Badge className="bg-purple-100 text-purple-700">Honorarios</Badge>
                          )}
                          {plantilla.categoria === 'atencion_cliente' && (
                            <Badge className="bg-cyan-100 text-cyan-700">At. Cliente</Badge>
                          )}
                          {plantilla.categoria === 'sentencia' && (
                            <Badge className="bg-emerald-100 text-emerald-700">Sentencia</Badge>
                          )}
                          {plantilla.categoria === 'recurso_apelacion_banco' && (
                            <Badge className="bg-orange-100 text-orange-700">Recurs apel·lació banc</Badge>
                          )}
                          {plantilla.categoria === 'senalamiento' && (
                            <Badge className="bg-blue-100 text-blue-700">Assenyalament</Badge>
                          )}
                          {!plantilla.activa && (
                            <Badge className="bg-gray-100 text-gray-500">Inactiva</Badge>
                          )}
                        </h3>
                        {plantilla.descripcion && (
                          <p className="text-sm text-gray-500">{plantilla.descripcion}</p>
                        )}
                        <p className="text-sm text-gray-600 mt-2">
                          <span className="font-medium">Asunto:</span> {plantilla.asunto}
                        </p>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {plantilla.cuerpo}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditPlantilla(plantilla)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletePlantilla(plantilla)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No hay plantillas configuradas</p>
                <Button variant="outline" className="mt-4" onClick={handleOpenNewPlantilla}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear plantilla
                </Button>
              </CardContent>
            </Card>
          )}
              </>
            );
          })()}
        </TabsContent>

        {/* TabsContent dummy para plantillas-automatizacion (el render real se hace en value="plantillas") */}
        <TabsContent value="plantillas-automatizacion" />
      </Tabs>

      {/* ================ DIALOG EMAIL CONFIG ================ */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              {editingConfig ? 'Editar Configuración SMTP' : 'Nueva Configuración SMTP'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitEmail} className="space-y-4">
            <div>
              <Label>Nombre de la configuración</Label>
              <Input
                value={emailForm.nombre}
                onChange={(e) => setEmailForm({...emailForm, nombre: e.target.value})}
                placeholder="Ej: Atención Cliente"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Servidor SMTP *</Label>
                <Input
                  value={emailForm.smtp_host}
                  onChange={(e) => setEmailForm({...emailForm, smtp_host: e.target.value})}
                  placeholder="smtp.ejemplo.com"
                  required
                />
              </div>
              <div>
                <Label>Puerto *</Label>
                <Input
                  type="number"
                  value={emailForm.smtp_port}
                  onChange={(e) => setEmailForm({...emailForm, smtp_port: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Usuario SMTP *</Label>
                <Input
                  value={emailForm.smtp_user}
                  onChange={(e) => setEmailForm({...emailForm, smtp_user: e.target.value})}
                  placeholder="usuario@ejemplo.com"
                  required
                />
              </div>
              <div>
                <Label>Contraseña {editingConfig ? '' : '*'}</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={emailForm.smtp_password}
                    onChange={(e) => setEmailForm({...emailForm, smtp_password: e.target.value})}
                    placeholder={editingConfig ? '(sin cambios)' : '********'}
                    required={!editingConfig}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="ssl"
                  checked={emailForm.smtp_use_ssl}
                  onCheckedChange={(v) => setEmailForm({...emailForm, smtp_use_ssl: v, smtp_use_tls: v ? false : emailForm.smtp_use_tls})}
                />
                <Label htmlFor="ssl">Usar SSL</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="tls"
                  checked={emailForm.smtp_use_tls}
                  onCheckedChange={(v) => setEmailForm({...emailForm, smtp_use_tls: v, smtp_use_ssl: v ? false : emailForm.smtp_use_ssl})}
                />
                <Label htmlFor="tls">Usar TLS</Label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email remitente *</Label>
                <Input
                  type="email"
                  value={emailForm.email_from}
                  onChange={(e) => setEmailForm({...emailForm, email_from: e.target.value})}
                  placeholder="info@lexduo.cat"
                  required
                />
              </div>
              <div>
                <Label>Nombre remitente</Label>
                <Input
                  value={emailForm.nombre_from}
                  onChange={(e) => setEmailForm({...emailForm, nombre_from: e.target.value})}
                  placeholder="LEXDUO"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={savingEmail}>
                {savingEmail && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ================ DIALOG PLANTILLA ================ */}
      <Dialog open={isPlantillaDialogOpen} onOpenChange={setIsPlantillaDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-plantilla">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitPlantilla} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de la plantilla *</Label>
                <Input
                  value={plantillaForm.nombre}
                  onChange={(e) => setPlantillaForm({...plantillaForm, nombre: e.target.value})}
                  placeholder="Ej: Respuesta inicial"
                  required
                />
              </div>
              <div>
                <Label>Categoría</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={plantillaForm.categoria || 'general'}
                  onChange={(e) => setPlantillaForm({...plantillaForm, categoria: e.target.value})}
                >
                  <option value="general">General</option>
                  <option value="atencion_cliente">Atención al Cliente</option>
                  <option value="honorarios">Honorarios</option>
                  <option value="sentencia">Sentencia</option>
                  <option value="recurso_apelacion_banco">Recurso apel·lació banc</option>
                  <option value="senalamiento">Assenyalament</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Idioma</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={plantillaForm.idioma || 'ca'}
                  onChange={(e) => setPlantillaForm({...plantillaForm, idioma: e.target.value})}
                  data-testid="plantilla-idioma"
                >
                  <option value="ca">Català</option>
                  <option value="es">Castellano</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={plantillaForm.orden}
                  onChange={(e) => setPlantillaForm({...plantillaForm, orden: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div>
              <Label>Descripción</Label>
              <Input
                value={plantillaForm.descripcion}
                onChange={(e) => setPlantillaForm({...plantillaForm, descripcion: e.target.value})}
                placeholder="Descripción breve"
              />
            </div>

            <div>
              <Label>Asunto del email *</Label>
              <Input
                value={plantillaForm.asunto}
                onChange={(e) => setPlantillaForm({...plantillaForm, asunto: e.target.value})}
                placeholder="Re: Su consulta en LEXDUO"
                required
              />
            </div>

            <div>
              <Label>Cuerpo del email *</Label>
              <RichTextEditor
                value={plantillaForm.cuerpo}
                onChange={(html) => setPlantillaForm({...plantillaForm, cuerpo: html})}
                placeholder="Estimado/a {{cliente_nombre}}, ..."
              />

              {/* v38: Repositorio visual de variables disponibles */}
              <div className="mt-3 border border-slate-200 rounded-lg bg-slate-50/60" data-testid="variables-repository">
                <button
                  type="button"
                  onClick={() => setVariablesOpen(!variablesOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100 rounded-t-lg transition-colors"
                  data-testid="toggle-variables"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    Variables disponibles para esta plantilla
                    <Badge variant="outline" className="text-[10px] ml-1 font-normal">
                      {variablesParaCategoria(plantillaForm.categoria || 'general').length}
                    </Badge>
                  </span>
                  {variablesOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </button>
                {variablesOpen && (
                  <div className="px-3 pb-3 space-y-2">
                    <p className="text-xs text-slate-500">
                      Click en una variable para copiarla y luego pégala (Ctrl+V) dentro del cuerpo o el asunto del email.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {variablesParaCategoria(plantillaForm.categoria || 'general').map((v) => {
                        const Icon = v.icon || Type;
                        return (
                          <button
                            type="button"
                            key={v.var}
                            onClick={() => copiarVariable(v.var)}
                            className={`group flex items-start gap-2 px-2.5 py-2 rounded-md border text-left hover:shadow-sm transition-all ${v.color} ${v.highlight ? 'ring-1 ring-amber-300 ring-offset-1' : ''}`}
                            data-testid={`var-chip-${v.var}`}
                            title={`Click para copiar {{${v.var}}}`}
                          >
                            <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <code className="text-[11px] font-mono font-semibold">{`{{${v.var}}}`}</code>
                                <Copy className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                              </div>
                              <div className="text-[11px] mt-0.5 leading-snug opacity-90">
                                <span className="font-medium">{v.label}</span> — {v.desc}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-500 italic pt-1 border-t border-slate-200">
                      <Sparkles className="w-3 h-3 inline mr-1 text-amber-500" />
                      Si omites <code className="font-mono">{`{{resumen_ia}}`}</code> en plantillas de Automatización,
                      el sistema insertará automáticamente el análisis IA antes de la firma como red de seguridad.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="activa"
                checked={plantillaForm.activa}
                onCheckedChange={(v) => setPlantillaForm({...plantillaForm, activa: v})}
              />
              <Label htmlFor="activa">Plantilla activa</Label>
            </div>

            <DialogFooter className="sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 -mb-6 pb-4">
              <Button type="button" variant="outline" onClick={() => setIsPlantillaDialogOpen(false)} data-testid="btn-cancelar-plantilla">
                Cancelar
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={savingPlantilla} data-testid="btn-guardar-plantilla">
                {savingPlantilla && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionAtencionCliente;
